import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "components/Button";
import useMiroConnection from "hooks/useMiroConnection";
import { useSnackbar } from "hooks/useSnackbar";
import { ACCOUNT_INTEGRATIONS_PATH } from "pages/Account/accountMenu";
import IntegrationCard from "pages/IntegrationsPage/IntegrationCard";
import { trackEvent } from "utils/analytics";
import { EVENTS } from "utils/analytics.events";

/**
 * Account → Integrations → Miro (REF-6702). Sits next to the Browzwear form.
 *
 * Hidden unless the `miro` module is visible+enabled AND the BE reports the integration available
 * (`available: false` = no Miro app configured in this environment). The connection lives on the
 * server; this card only reflects `GET integrations/miro/status` and offers connect / reconnect /
 * disconnect.
 */
interface MiroConnectionCardProps {
  /** Where the OAuth round-trip lands afterwards (an in-app path; the callback page re-validates it). Defaults to Account → Integrations. */
  returnTo?: string;
  /** Surface label for analytics. */
  surface?: string;
  /** A connection the host already owns (so its own status stays in step after Connect / Disconnect). */
  connection?: ReturnType<typeof useMiroConnection>;
  /** Extra body content under the status (the hub's "Where to use Miro" list). */
  children?: React.ReactNode;
}

const MiroConnectionCard = ({ returnTo = ACCOUNT_INTEGRATIONS_PATH, surface = "account", connection: hostConnection, children }: MiroConnectionCardProps) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const ownConnection = useMiroConnection(!hostConnection);
  const connection = hostConnection ?? ownConnection;
  const [isBusy, setIsBusy] = useState(false);

  const handleConnect = useCallback(async () => {
    setIsBusy(true);
    trackEvent(EVENTS.MIRO.CONNECT_START, { surface });
    // Come back to the page this card sits on — the callback page restores `return_to`.
    const outcome = await connection.connect(returnTo);
    if (outcome === "redirected") return;
    setIsBusy(false);
    if (outcome === "failed") showError(t("miro:errors.connect_failed"));
  }, [connection, returnTo, surface, showError, t]);

  const handleDisconnect = useCallback(async () => {
    setIsBusy(true);
    const ok = await connection.disconnect();
    setIsBusy(false);

    if (ok) {
      trackEvent(EVENTS.MIRO.DISCONNECT);
      showSuccess(t("miro:account.disconnected"));
    } else {
      showError(t("miro:errors.disconnect_failed"));
    }
  }, [connection, showError, showSuccess, t]);

  if (!connection.isLoading && !connection.isAvailable) return null;

  const { status } = connection;
  const who = [status.miro_user?.name, status.miro_team?.name].filter(Boolean).join(" · ");
  const ready = !connection.isLoading && connection.state !== "error";
  const chip = !ready
    ? undefined
    : connection.needsReauth
    ? { text: t("miro:hub.status_reconnect"), tone: "warn" as const }
    : connection.isConnected
    ? { text: <span data-testid="miro-connected-as">{t("miro:account.connected_as", { who: who || t("miro:account.unknown_user") })}</span>, tone: "ok" as const }
    : { text: t("account:integrations.status_not_connected"), tone: "idle" as const };

  return (
    <IntegrationCard
      data-testid="miro-connection-card"
      brand="miro"
      name={t("miro:account.title")}
      description={t("miro:hub.description")}
      status={chip?.text}
      statusTone={chip?.tone}
      actions={
        <>
          {connection.status.connected && !connection.isLoading && (
            <Button data-testid="miro-disconnect-btn" className="btn-secondary-small" disabled={isBusy} onClick={handleDisconnect}>
              {t("miro:account.disconnect")}
            </Button>
          )}
          {!connection.isLoading && connection.state !== "error" && !connection.isConnected && (
            <Button data-testid="miro-connect-btn" className="btn-primary-small" disabled={isBusy} onClick={handleConnect}>
              {connection.needsReauth ? t("miro:account.reconnect") : t("miro:account.connect")}
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-2" data-testid="miro-connection-body">
        {connection.isLoading ? (
          <span className="text-white/50">{t("miro:account.loading")}</span>
        ) : connection.state === "error" ? (
          <>
            <span className="text-orange">{t("miro:errors.status_failed")}</span>
            <Button data-testid="miro-retry-status-btn" className="btn-secondary-small self-start" onClick={() => void connection.refresh()}>
              {t("miro:picker.retry")}
            </Button>
          </>
        ) : connection.needsReauth ? (
          <span className="text-orange" data-testid="miro-needs-reauth">
            {t("miro:account.needs_reauth")}
          </span>
        ) : null}
        {children}
      </div>
    </IntegrationCard>
  );
};

export default MiroConnectionCard;
